import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { getDatabase, ref, onValue, off, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import {
  Box, Typography, Grid, Paper, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem, Select,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, CircularProgress, Divider, Alert
} from '@mui/material';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';

const useStyles = makeStyles({
  root: { padding: 24 },
  title: { fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: 22, marginBottom: 16, color: MAIN_COLOR },
  card: { padding: 16, borderRadius: 12, textAlign: 'center' },
  cardValue: { fontFamily: FONT_FAMILY, fontSize: 28, fontWeight: 700, color: MAIN_COLOR },
  cardLabel: { fontFamily: FONT_FAMILY, fontSize: 13, color: '#666', marginTop: 4 },
  tableHead: { backgroundColor: MAIN_COLOR },
  tableHeadCell: { color: '#fff', fontFamily: FONT_FAMILY, fontWeight: 700 },
  chip: { fontFamily: FONT_FAMILY, fontSize: 12 },
  actionBtn: { marginRight: 8, fontFamily: FONT_FAMILY, textTransform: 'none' },
  filterRow: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
  sectionTitle: { fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: 15, marginBottom: 8, marginTop: 16 },
  detailLabel: { fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: 13, color: '#555' },
  detailValue: { fontFamily: FONT_FAMILY, fontSize: 13 },
});

const STATUS_COLORS = {
  COMPLETED: 'success', MANUALLY_APPROVED: 'success',
  PENDING_REVIEW: 'warning', WAITING_USER_CONFIRMATION: 'info',
  RECEIVED: 'default', PROCESSING: 'default',
  DUPLICATE: 'secondary', MANUALLY_REJECTED: 'error',
  REJECTED: 'error', ERROR: 'error',
};

const serverUrl = 'https://europe-west1-sawiride.cloudfunctions.net';

export default function LocalPayments() {
  const classes = useStyles();
  const settings = useSelector(state => state.settingsdata.settings);

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProvider, setFilterProvider] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchText, setSearchText] = useState('');
  const [approveError, setApproveError] = useState('');
  const [selectedTx, setSelectedTx] = useState(null);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});

  useEffect(() => {
    const db = getDatabase();
    const txRef = ref(db, 'localPaymentTransactions');
    const listener = onValue(txRef, (snap) => {
      const val = snap.val();
      if (val) {
        const arr = Object.entries(val).map(([k, v]) => ({ _key: k, ...v }));
        arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setTransactions(arr);
      } else {
        setTransactions([]);
      }
      setLoading(false);
    }, (error) => {
      console.error('localPaymentTransactions read error:', error.message);
      setLoading(false);
    });
    return () => off(txRef, 'value', listener);
  }, []);

  useEffect(() => {
    const uids = [...new Set(transactions.map(t => t.matchedUserId || t.userId).filter(Boolean))];
    if (uids.length === 0) return;
    const db = getDatabase();
    uids.forEach(uid => {
      if (userProfiles[uid]) return;
      get(ref(db, 'users/' + uid)).then(snap => {
        if (snap.exists()) {
          const u = snap.val();
          setUserProfiles(prev => ({ ...prev, [uid]: { firstName: u.firstName || '', lastName: u.lastName || '', mobile: u.mobile || '' } }));
        }
      }).catch(() => {});
    });
  }, [transactions]);

  const getToken = async () => {
    const user = getAuth().currentUser;
    if (!user) return null;
    return await user.getIdToken();
  };

  const doApprove = async () => {
    if (!selectedTx || !targetUserId || !adminNote) return;
    setActionLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(serverUrl + '/manualApproveLocalPayment', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, idempotencyKey: selectedTx._key, targetUserId, reason: adminNote }),
      });
      const data = await res.json();
      if (data.success) { setApproveDialog(false); setSelectedTx(null); setTargetUserId(''); setAdminNote(''); setApproveError(''); }
      else setApproveError(data.error || 'Approval failed.');
    } catch (e) { setApproveError('Network error: ' + e.message); }
    setActionLoading(false);
  };

  const doReject = async () => {
    if (!selectedTx) return;
    setActionLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(serverUrl + '/manualRejectLocalPayment', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, idempotencyKey: selectedTx._key, reason: adminNote }),
      });
      const data = await res.json();
      if (data.success) { setRejectDialog(false); setSelectedTx(null); setAdminNote(''); }
      else alert(data.error || 'Failed');
    } catch (e) { alert('Error: ' + e.message); }
    setActionLoading(false);
  };

  const now = Date.now();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(); monthAgo.setDate(monthAgo.getDate() - 30);

  const completedTx = transactions.filter(t => t.status === 'COMPLETED' || t.status === 'MANUALLY_APPROVED');
  const symbol = settings && settings.symbol ? settings.symbol : 'MRU';

  const sumFor = (list, after) =>
    list.filter(t => (t.createdAt || 0) >= after.getTime()).reduce((s, t) => s + (t.amount || 0), 0);

  const countFor = (list, provider, after) =>
    list.filter(t => t.provider === provider && (t.createdAt || 0) >= after.getTime()).reduce((s, t) => s + (t.amount || 0), 0);

  const filtered = transactions.filter(t => {
    if (filterProvider !== 'ALL' && t.provider !== filterProvider) return false;
    if (filterStatus !== 'ALL' && t.status !== filterStatus) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      const profile = userProfiles[t.matchedUserId || t.userId];
      const profileName = profile ? [profile.firstName, profile.lastName].filter(Boolean).join(' ').toLowerCase() : '';
      const profilePhone = profile ? (profile.mobile || '').toLowerCase() : '';
      const matches =
        (t.senderPhone || '').toLowerCase().includes(q) ||
        (t.transactionId || '').toLowerCase().includes(q) ||
        (t.matchedUserId || '').toLowerCase().includes(q) ||
        (t.userId || '').toLowerCase().includes(q) ||
        (t.userName || '').toLowerCase().includes(q) ||
        (t.receiverNumber || '').toLowerCase().includes(q) ||
        profileName.includes(q) ||
        profilePhone.includes(q);
      if (!matches) return false;
    }
    return true;
  });

  const statuses = [...new Set(transactions.map(t => t.status))];

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box className={classes.root}>
      <Typography className={classes.title}>Local Payments Dashboard</Typography>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Today — Bankily', value: countFor(completedTx, 'BANKILY', today) },
          { label: 'Today — BimBank', value: countFor(completedTx, 'BIMBANK', today) },
          { label: 'Today — Sedad', value: countFor(completedTx, 'SEDAD', today) },
          { label: 'Today Total', value: sumFor(completedTx, today) },
          { label: 'Week Total', value: sumFor(completedTx, weekAgo) },
          { label: 'Month Total', value: sumFor(completedTx, monthAgo) },
          { label: 'Pending Review', value: transactions.filter(t => t.status === 'PENDING_REVIEW').length + ' txs', isCount: true },
          { label: 'Waiting Confirm', value: transactions.filter(t => t.status === 'WAITING_USER_CONFIRMATION').length + ' txs', isCount: true },
        ].map((s, i) => (
          <Grid item xs={6} sm={4} md={3} key={i}>
            <Paper className={classes.card} elevation={2}>
              <Typography className={classes.cardValue}>{s.isCount ? s.value : symbol + ' ' + (s.value || 0).toFixed(0)}</Typography>
              <Typography className={classes.cardLabel}>{s.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box className={classes.filterRow}>
        <FormControl size="small" style={{ minWidth: 130 }}>
          <InputLabel style={{ fontFamily: FONT_FAMILY }}>Provider</InputLabel>
          <Select value={filterProvider} label="Provider" onChange={e => setFilterProvider(e.target.value)} style={{ fontFamily: FONT_FAMILY }}>
            <MenuItem value="ALL">All</MenuItem>
            {['BANKILY', 'BIMBANK', 'SEDAD'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" style={{ minWidth: 160 }}>
          <InputLabel style={{ fontFamily: FONT_FAMILY }}>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)} style={{ fontFamily: FONT_FAMILY }}>
            <MenuItem value="ALL">All</MenuItem>
            {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Search (name, phone, Tx ID…)" value={searchText} onChange={e => setSearchText(e.target.value)}
          style={{ minWidth: 220 }}
          InputProps={{ style: { fontFamily: FONT_FAMILY } }} InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }} />
        <Typography style={{ fontFamily: FONT_FAMILY, color: '#888', fontSize: 13 }}>{filtered.length} result(s)</Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              {['Time', 'Provider', 'Sender', 'Amount', 'Tx ID', 'Status', 'Actions'].map(h => (
                <TableCell key={h} className={classes.tableHeadCell}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} style={{ textAlign: 'center', fontFamily: FONT_FAMILY, color: '#888', padding: 24 }}>No transactions found</TableCell></TableRow>
            )}
            {filtered.map((tx) => (
              <TableRow key={tx._key} hover style={{ cursor: 'pointer' }} onClick={() => setSelectedTx(tx)}>
                <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: 12 }}>{tx.createdAt ? new Date(tx.createdAt).toLocaleString() : '-'}</TableCell>
                <TableCell style={{ fontFamily: FONT_FAMILY }}>{tx.provider || '-'}</TableCell>
                <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: 12 }}>{tx.senderPhone || '-'}</TableCell>
                <TableCell style={{ fontFamily: FONT_FAMILY }}>{tx.amount} MRU</TableCell>
                <TableCell style={{ fontFamily: FONT_FAMILY, fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{tx.transactionId || '-'}</TableCell>
                <TableCell>
                  <Chip className={classes.chip} label={tx.status} color={STATUS_COLORS[tx.status] || 'default'} size="small" />
                  {tx.source === 'customer_confirmed_no_collector' && settings && settings.localPaymentsAutoCreditEnabled && (
                    <Chip size="small" label="⚠ Verify" style={{ marginLeft: 4, backgroundColor: '#FEF3C7', color: '#92400E', fontFamily: FONT_FAMILY, fontSize: 11 }} />
                  )}
                </TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  {(tx.status === 'PENDING_REVIEW' || tx.status === 'WAITING_USER_CONFIRMATION') && (
                    <>
                      <Button size="small" variant="contained" color="success" className={classes.actionBtn}
                        onClick={() => { setSelectedTx(tx); setTargetUserId(tx.matchedUserId || ''); setApproveError(''); setApproveDialog(true); }}>Approve</Button>
                      <Button size="small" variant="outlined" color="error" className={classes.actionBtn}
                        onClick={() => { setSelectedTx(tx); setRejectDialog(true); }}>Reject</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      {selectedTx && !approveDialog && !rejectDialog && (
        <Dialog open onClose={() => setSelectedTx(null)} maxWidth="sm" fullWidth>
          <DialogTitle style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>Transaction Detail</DialogTitle>
          <DialogContent dividers>
            {selectedTx.source === 'customer_confirmed_no_collector' && settings && settings.localPaymentsAutoCreditEnabled && (
              <Alert severity="warning" style={{ marginBottom: 16, fontFamily: FONT_FAMILY }}>
                User confirmed payment in app but no bank notification was received. Check your banking app to verify this amount was actually received before approving.
              </Alert>
            )}
            {[
              ['Provider', selectedTx.provider], ['Status', selectedTx.status], ['Amount', selectedTx.amount + ' MRU'],
              ['Sender Phone', selectedTx.senderPhone], ['Normalized Phone', selectedTx.normalizedSenderPhone],
              ['Transaction ID', selectedTx.transactionId], ['Receiver Number', selectedTx.receiverNumber],
              ['Device ID', selectedTx.deviceId], ['Package', selectedTx.notificationPackage],
              ['Parser Confidence', selectedTx.parserConfidence], ['Matched User', selectedTx.matchedUserId],
              ['User Name', (() => { const u = userProfiles[selectedTx.matchedUserId || selectedTx.userId]; return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || null : null; })()],
              ['User Phone', (() => { const u = userProfiles[selectedTx.matchedUserId || selectedTx.userId]; return u ? u.mobile || null : null; })()],
              ['Matched Top-up', selectedTx.matchedTopupId], ['Failure Reason', selectedTx.failureReason],
              ['Admin Notes', selectedTx.adminNotes], ['Created', selectedTx.createdAt ? new Date(selectedTx.createdAt).toLocaleString() : '-'],
            ].map(([label, value]) => value ? (
              <Box key={label} mb={1}>
                <Typography className={classes.detailLabel}>{label}</Typography>
                <Typography className={classes.detailValue}>{value}</Typography>
                <Divider style={{ marginTop: 4 }} />
              </Box>
            ) : null)}
            {selectedTx.rawNotification && (
              <Box mt={1}>
                <Typography className={classes.detailLabel}>Raw Notification</Typography>
                <Typography className={classes.detailValue} style={{ fontSize: 11, whiteSpace: 'pre-wrap', backgroundColor: '#f5f5f5', padding: 8, borderRadius: 4 }}>{selectedTx.rawNotification}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {(selectedTx.status === 'PENDING_REVIEW' || selectedTx.status === 'WAITING_USER_CONFIRMATION') && (
              <>
                <Button color="success" variant="contained" onClick={() => { setTargetUserId(selectedTx?.matchedUserId || ''); setApproveError(''); setApproveDialog(true); }} style={{ fontFamily: FONT_FAMILY }}>Approve & Credit</Button>
                <Button color="error" variant="outlined" onClick={() => setRejectDialog(true)} style={{ fontFamily: FONT_FAMILY }}>Reject</Button>
              </>
            )}
            <Button onClick={() => setSelectedTx(null)} style={{ fontFamily: FONT_FAMILY }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onClose={() => { setApproveDialog(false); setApproveError(''); }} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>Approve & Credit Wallet</DialogTitle>
        <DialogContent>
          {selectedTx?.source === 'customer_confirmed_no_collector' && settings && settings.localPaymentsAutoCreditEnabled && (
            <Alert severity="warning" style={{ marginBottom: 12, fontFamily: FONT_FAMILY }}>
              No bank notification received. Verify money was actually received before approving.
            </Alert>
          )}
          <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, marginBottom: 12 }}>
            This will credit <strong>{selectedTx?.amount} MRU</strong> to the target user's wallet. This cannot be undone.
          </Typography>
          {selectedTx && userProfiles[selectedTx.matchedUserId || selectedTx.userId] && (
            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, marginBottom: 8, color: '#065F46', backgroundColor: '#ECFDF5', padding: '6px 10px', borderRadius: 6 }}>
              {[userProfiles[selectedTx.matchedUserId || selectedTx.userId].firstName, userProfiles[selectedTx.matchedUserId || selectedTx.userId].lastName].filter(Boolean).join(' ')}
              {userProfiles[selectedTx.matchedUserId || selectedTx.userId].mobile ? ' · ' + userProfiles[selectedTx.matchedUserId || selectedTx.userId].mobile : ''}
            </Typography>
          )}
          <TextField fullWidth label="Target User ID" value={targetUserId} onChange={e => setTargetUserId(e.target.value)}
            margin="normal" size="small"
            helperText={(() => {
              const u = selectedTx && userProfiles[selectedTx.matchedUserId || selectedTx.userId];
              const name = u ? [u.firstName, u.lastName].filter(Boolean).join(' ') : '';
              const phone = u ? u.mobile : '';
              if (name || phone) return [name, phone].filter(Boolean).join(' · ');
              return "User UID from Firebase";
            })()}
            InputProps={{ style: { fontFamily: FONT_FAMILY } }} InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }} />
          <TextField fullWidth label="Approval reason (required)" value={adminNote} onChange={e => setAdminNote(e.target.value)}
            margin="normal" size="small" multiline rows={2}
            InputProps={{ style: { fontFamily: FONT_FAMILY } }} InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }} />
          {approveError && (
            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: '#DC2626', backgroundColor: '#FEF2F2', padding: '8px 10px', borderRadius: 6, marginTop: 8 }}>
              {approveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setApproveDialog(false); setApproveError(''); }} style={{ fontFamily: FONT_FAMILY }}>Cancel</Button>
          <Button onClick={doApprove} disabled={actionLoading || !targetUserId || !adminNote} variant="contained" color="success" style={{ fontFamily: FONT_FAMILY }}>
            {actionLoading ? <CircularProgress size={18} /> : 'Confirm Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>Reject Transaction</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Rejection reason" value={adminNote} onChange={e => setAdminNote(e.target.value)}
            margin="normal" size="small" multiline rows={2}
            InputProps={{ style: { fontFamily: FONT_FAMILY } }} InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} style={{ fontFamily: FONT_FAMILY }}>Cancel</Button>
          <Button onClick={doReject} disabled={actionLoading} variant="contained" color="error" style={{ fontFamily: FONT_FAMILY }}>
            {actionLoading ? <CircularProgress size={18} /> : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
